import { MeuTimHealthCheckOnDemandResponse } from '../services/clients/ClientService';

/**
 * Fatia 0 (GCM) — estados reais do verify Meu TIM on-demand. `loading` só existe
 * no hook (não vem da API); os demais espelham o enum do worker/API. Nunca existe
 * um estado "sucesso por ter enfileirado" — só resultado real do portal.
 */
export type MeuTimVerifyUiStatus =
  | 'idle'
  | 'loading'
  | 'ok'
  | 'credencial_invalida'
  | 'timeout'
  | 'bloqueio_ou_captcha'
  | 'erro_portal';

export interface MeuTimVerifyOnDemandResult {
  status: MeuTimVerifyUiStatus;
  message: string;
  /**
   * Soft-fail: true em qualquer status que não deva bloquear o fluxo (todos,
   * exceto os estados transitórios `idle`/`loading`). O caller decide o que
   * fazer com `credencial_invalida` (idealmente pedir credenciais novas), mas
   * nunca deve travar criação/operação por causa de timeout/captcha/erro portal.
   */
  canProceed: boolean;
  checkedAt: string | null;
}

function resultFor(
  status: MeuTimVerifyUiStatus,
  message: string,
  checkedAt: string | null,
): MeuTimVerifyOnDemandResult {
  return { status, message, canProceed: true, checkedAt };
}

/** Mapeia a resposta real do verify on-demand (fatia 0) para o estado de UI. Nunca mapeia "enfileirou" para sucesso. */
export function mapMeuTimHealthCheckResponse(
  response: MeuTimHealthCheckOnDemandResponse,
): MeuTimVerifyOnDemandResult {
  switch (response.status) {
    case 'ok':
      return resultFor('ok', response.message || 'Acesso Meu TIM confirmado.', response.checkedAt);
    case 'credencial_invalida':
      return resultFor(
        'credencial_invalida',
        response.message || 'Credenciais Meu TIM inválidas — solicite login e senha atualizados ao cliente.',
        response.checkedAt,
      );
    case 'bloqueio_ou_captcha':
      return resultFor(
        'bloqueio_ou_captcha',
        response.message ||
          'O portal Meu TIM bloqueou a verificação (captcha). Você pode continuar; tente verificar novamente mais tarde.',
        response.checkedAt,
      );
    case 'timeout':
      return resultFor(
        'timeout',
        response.message ||
          'A verificação excedeu o tempo limite (45s). Você pode continuar; tente novamente mais tarde.',
        response.checkedAt,
      );
    case 'erro_portal':
    case 'nao_testada':
    default:
      return resultFor(
        'erro_portal',
        response.message || 'Não foi possível verificar o acesso Meu TIM agora. Você pode continuar; tente novamente mais tarde.',
        response.checkedAt,
      );
  }
}

/** Mapeia falhas de rede/timeout do próprio cliente (axios) — sempre soft-fail. */
export function mapMeuTimVerifyOnDemandError(error: unknown): MeuTimVerifyOnDemandResult {
  const err = error as { code?: string; message?: string } | undefined;
  const isTimeout =
    err?.code === 'ECONNABORTED' || (typeof err?.message === 'string' && err.message.toLowerCase().includes('timeout'));

  if (isTimeout) {
    return resultFor(
      'timeout',
      'A verificação excedeu o tempo limite (45s). Você pode continuar; tente novamente mais tarde.',
      null,
    );
  }

  return resultFor(
    'erro_portal',
    'Não foi possível verificar o acesso Meu TIM agora (falha de rede). Você pode continuar; tente novamente mais tarde.',
    null,
  );
}
